import { CalendarEvent, RecurringUnit } from "protogen/calendar_pb";
import { RecurrenceData } from "components/calendar/CustomRecurrence";

export type ReminderRecurrence = {
  recurringUnit: RecurringUnit;
  recurringInterval: number;
  recurringUntilSecUTC: bigint;
  recurringByDay: number[];
  recurringCount: number;
  recurringByMonthDay: number[];
  recurringBySetPos: number;
};

const UNIT_TO_PROTO: Record<RecurrenceData["unit"], RecurringUnit> = {
  day: RecurringUnit.RecurringUnit_DAY,
  week: RecurringUnit.RecurringUnit_WEEK,
  month: RecurringUnit.RecurringUnit_MONTH,
  year: RecurringUnit.RecurringUnit_YEAR,
};

const PROTO_TO_UNIT: Partial<Record<RecurringUnit, RecurrenceData["unit"]>> = {
  [RecurringUnit.RecurringUnit_DAY]: "day",
  [RecurringUnit.RecurringUnit_WEEK]: "week",
  [RecurringUnit.RecurringUnit_MONTH]: "month",
  [RecurringUnit.RecurringUnit_YEAR]: "year",
};

export const isRecurringReminder = (event: CalendarEvent): boolean =>
  event.recurringUnit !== RecurringUnit.RecurringUnit_UNSPECIFIED &&
  event.recurringInterval > 0;

// Monthly patterns are derived from the reminder's start date at save time, so moving the date
// keeps "the 2nd Tuesday" or "the 14th" in sync with it.
export const toReminderRecurrence = (
  data: RecurrenceData,
  startDate: Date,
): ReminderRecurrence => {
  let recurringByDay: number[] = [];
  let recurringByMonthDay: number[] = [];
  let recurringBySetPos = 0;
  if (data.unit === "week") {
    recurringByDay = data.byDay;
  } else if (data.unit === "month" && data.monthlyPattern === "dayOfMonth") {
    recurringByMonthDay = [startDate.getDate()];
  } else if (data.unit === "month" && data.monthlyPattern === "dayOfWeek") {
    recurringBySetPos = Math.ceil(startDate.getDate() / 7);
    recurringByDay = [startDate.getDay()];
  }

  let recurringUntilSecUTC = BigInt(0);
  if (data.endType === "on" && data.endDate) {
    const endOfDay = new Date(data.endDate);
    endOfDay.setHours(23, 59, 59, 999);
    recurringUntilSecUTC = BigInt(Math.ceil(endOfDay.getTime() / 1000));
  }

  return {
    recurringUnit: UNIT_TO_PROTO[data.unit],
    recurringInterval: data.interval,
    recurringUntilSecUTC,
    recurringByDay,
    recurringCount: data.endType === "after" && data.count ? data.count : 0,
    recurringByMonthDay,
    recurringBySetPos,
  };
};

export const toRecurrenceData = (
  event: CalendarEvent,
): RecurrenceData | undefined => {
  const unit = PROTO_TO_UNIT[event.recurringUnit];
  if (!unit || !isRecurringReminder(event)) return undefined;

  let monthlyPattern: RecurrenceData["monthlyPattern"];
  if (unit === "month") {
    monthlyPattern = event.recurringBySetPos > 0 ? "dayOfWeek" : "dayOfMonth";
  }
  const untilSec = Number(event.recurringUntilSec);
  return {
    interval: event.recurringInterval,
    unit,
    byDay: unit === "week" ? [...event.recurringByDay] : [],
    monthlyPattern,
    endType: event.recurringCount > 0 ? "after" : untilSec > 0 ? "on" : "never",
    endDate: untilSec > 0 ? new Date(untilSec * 1000) : undefined,
    count: event.recurringCount || undefined,
  };
};

const sameNumbers = (a: number[], b: number[]) =>
  a.length === b.length && a.every((n, i) => n === b[i]);

export const hasRecurrenceChanged = (
  event: CalendarEvent,
  recurrence: ReminderRecurrence | null,
): boolean => {
  if (!recurrence) return isRecurringReminder(event);
  return (
    event.recurringUnit !== recurrence.recurringUnit ||
    event.recurringInterval !== recurrence.recurringInterval ||
    event.recurringUntilSec !== recurrence.recurringUntilSecUTC ||
    event.recurringCount !== recurrence.recurringCount ||
    event.recurringBySetPos !== recurrence.recurringBySetPos ||
    !sameNumbers(event.recurringByDay, recurrence.recurringByDay) ||
    !sameNumbers(event.recurringByMonthDay, recurrence.recurringByMonthDay)
  );
};
